import {
  ActivityKind,
  AnyActivity,
  isActivityKind,
  isImportance,
  isSignalKind,
  SIGNAL_KIND_TAGS,
  SignalTag
} from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { MAX_ORACLE_CIDS } from '@blisspointmedia/bpm-types/dist/OracleApi';
import { SortBy, URL_PARAMS } from '../activity-feed-constants';
import {
  ActivityFeedFilterState,
  DatePreset,
  DEFAULT_FILTER_STATE,
  IMPORTANCE_ORDER,
  isDatePreset
} from './activity-feed-filters-constants';
import { MILLISECONDS_IN_A_DAY } from '../../../utils/date-utils';
import { isEventTheme } from '../oracle-taxonomy-constants';
import { getEventTheme } from '../oracle-taxonomy-utils';

const toISODate = (d: Date): string => d.toISOString().slice(0, 10);

// Handles both Date objects (in tests/runtime) and ISO strings (from JSON deserialization).
const eventDateString = (date: Date | string): string =>
  date instanceof Date ? date.toISOString().slice(0, 10) : String(date).slice(0, 10);
const getToday = (): string => toISODate(new Date());
const getTomorrow = (): string => toISODate(new Date(Date.now() + MILLISECONDS_IN_A_DAY));
const getLast7 = (): string => toISODate(new Date(Date.now() - 6 * MILLISECONDS_IN_A_DAY));
const getLast30 = (): string => toISODate(new Date(Date.now() - 29 * MILLISECONDS_IN_A_DAY));

const getParamArray = (params: URLSearchParams, key: string): string[] => {
  const raw = params.get(key);
  return raw ? raw.split(',').filter(Boolean) : [];
};

const setParamArray = (params: URLSearchParams, key: string, values: string[]): void => {
  if (values.length > 0) {
    params.set(key, values.join(','));
  }
};

const VALID_SORT_VALUES = new Set<string>(Object.values(SortBy));

export const paramsToSortBy = (params: URLSearchParams): SortBy | undefined => {
  const sort = params.get(URL_PARAMS.SORT);
  return sort !== null && VALID_SORT_VALUES.has(sort) ? (sort as SortBy) : undefined;
};

// All URL keys owned by the filter+sort layer — everything except the selected-item cursor.
export const FILTER_PARAM_KEYS = Object.values(URL_PARAMS).filter(
  key => key !== URL_PARAMS.SELECTED
);

export const filterParamsKey = (params: URLSearchParams): string => {
  const filterParams = new URLSearchParams();
  for (const key of FILTER_PARAM_KEYS) {
    for (const value of params.getAll(key)) {
      filterParams.append(key, value);
    }
  }
  return filterParams.toString();
};

// Filter params round-trip through parse → serialize; sort is kept only when it's a valid SortBy.
export const sanitizeFilterParams = (params: URLSearchParams): URLSearchParams => {
  const sanitized = filterStateToParams(paramsToFilterState(params));
  const sort = paramsToSortBy(params);
  if (sort !== undefined) {
    sanitized.set(URL_PARAMS.SORT, sort);
  }
  return sanitized;
};

export const paramsToClients = (params: URLSearchParams): string[] =>
  getParamArray(params, URL_PARAMS.CLIENTS).slice(0, MAX_ORACLE_CIDS);

export const clientsToParams = (params: URLSearchParams, cids: string[]): void =>
  setParamArray(params, URL_PARAMS.CLIENTS, cids);

// Order-insensitive: the feed treats the same set of clients in any order as one selection.
export const clientsKey = (cids: readonly string[]): string => [...cids].sort().join(',');

export const sameClients = (a: readonly string[], b: readonly string[]): boolean =>
  a.length === b.length && clientsKey(a) === clientsKey(b);

// The Clients filter keeps edits in a draft and commits them to the URL when its menu closes.
// Clearing commits right away, because the clear button works while the menu is closed.
export interface ClientsDraftChange {
  draft: string[];
  commit: string[] | null;
}

export const applyClientsDraftChange = (
  next: readonly string[],
  selected: readonly string[]
): ClientsDraftChange => {
  const draft = next.slice(0, MAX_ORACLE_CIDS);
  const commit = draft.length === 0 && !sameClients(draft, selected) ? draft : null;
  return { draft, commit };
};

export const clientsToCommitOnClose = (
  draft: readonly string[],
  selected: readonly string[]
): string[] | null => (sameClients(draft, selected) ? null : [...draft]);

export const paramsToFilterState = (params: URLSearchParams): ActivityFeedFilterState => {
  const dateParam = params.get(URL_PARAMS.DATE);
  const date = isDatePreset(dateParam) ? dateParam : null;

  return {
    ...DEFAULT_FILTER_STATE,
    clients: paramsToClients(params),
    date,
    customStartDate: date === DatePreset.CUSTOM ? params.get(URL_PARAMS.DATE_START) : null,
    customEndDate: date === DatePreset.CUSTOM ? params.get(URL_PARAMS.DATE_END) : null,
    importance: getParamArray(params, URL_PARAMS.IMPORTANCE).filter(isImportance),
    signals: getParamArray(params, URL_PARAMS.SIGNALS).filter(isSignalKind),
    categories: getParamArray(params, URL_PARAMS.CATEGORY).filter(isEventTheme),
    channels: getParamArray(params, URL_PARAMS.CHANNEL),
    kinds: parseKinds(params.get(URL_PARAMS.KINDS))
  };
};

// Absent param means the default kinds.
// Explicit empty string ("kinds=") preserves the "every kind toggled off" state
// through a URL round-trip.
// A non-empty param whose tokens are all unrecognized is treated as malformed
// and falls back to the default (rather than collapsing to all-off),
// so a typo'd URL doesn't strand the user on an empty feed.
const parseKinds = (raw: string | null): ActivityKind[] => {
  if (raw === null) return [...DEFAULT_FILTER_STATE.kinds];
  if (raw === '') return [];
  const valid = [...new Set(raw.split(',').filter(isActivityKind))];
  if (valid.length === 0) return [...DEFAULT_FILTER_STATE.kinds];
  return valid;
};

// Set-based array difference — order-agnostic, duplicate-agnostic.
const arraysDiffer = (val: readonly unknown[], def: readonly unknown[]): boolean => {
  if (val.length !== def.length) return true;
  const defSet: ReadonlySet<unknown> = new Set(def);
  return val.some(v => !defSet.has(v));
};

export const filterStateToParams = (state: ActivityFeedFilterState): URLSearchParams => {
  const params = new URLSearchParams();

  if (state.date !== null) {
    params.set(URL_PARAMS.DATE, state.date);
    if (state.date === DatePreset.CUSTOM) {
      if (state.customStartDate) {
        params.set(URL_PARAMS.DATE_START, state.customStartDate);
      }
      if (state.customEndDate) {
        params.set(URL_PARAMS.DATE_END, state.customEndDate);
      }
    }
  }

  clientsToParams(params, state.clients);
  setParamArray(params, URL_PARAMS.IMPORTANCE, state.importance);
  setParamArray(params, URL_PARAMS.SIGNALS, state.signals);
  setParamArray(params, URL_PARAMS.CATEGORY, state.categories);
  setParamArray(params, URL_PARAMS.CHANNEL, state.channels);
  if (arraysDiffer(state.kinds, DEFAULT_FILTER_STATE.kinds)) {
    // Explicit empty string preserves the "no kinds selected" state through a round-trip.
    params.set(URL_PARAMS.KINDS, state.kinds.join(','));
  }

  return params;
};

const getEffectiveDateRange = (
  state: ActivityFeedFilterState
): { start: string | null; end: string | null } => {
  if (state.date === null) return { start: null, end: null };
  if (state.date === DatePreset.TODAY) {
    const today = getToday();
    return { start: today, end: today };
  }
  if (state.date === DatePreset.LAST_7) return { start: getLast7(), end: getToday() };
  if (state.date === DatePreset.LAST_30) return { start: getLast30(), end: getToday() };
  if (state.date === DatePreset.UPCOMING) return { start: getTomorrow(), end: null };
  return { start: state.customStartDate, end: state.customEndDate };
};

const buildFilterChecks = (state: ActivityFeedFilterState) => {
  const { start: dateStart, end: dateEnd } = getEffectiveDateRange(state);
  const { clients, date, importance, signals, categories, channels, kinds } = state;
  // Hoist the signal-tag set once per filterState instead of flatMap+includes per item.
  const selectedSignalTags = new Set<SignalTag>(
    signals.flatMap(signal => SIGNAL_KIND_TAGS[signal])
  );

  // The kinds / clients / date / channel checks apply uniformly to every AnyActivity; only the
  // importance / signals / categories checks short-circuit for non-Events (those fields don't
  // exist on other kinds).
  return [
    // kinds is inclusionary — empty means "show nothing".
    (activity: AnyActivity) => kinds.includes(activity.kind),
    // Matches the primary `cid` only: Oracle returns one CID per activity, and the server already
    // scopes multi-CID requests by `cids`.
    (activity: AnyActivity) => clients.length === 0 || clients.includes(activity.cid),
    (activity: AnyActivity) => {
      if (date === null || dateStart === null) return true;
      const dateString = eventDateString(activity.date);
      return dateString >= dateStart && (dateEnd === null || dateString <= dateEnd);
    },
    (activity: AnyActivity) =>
      importance.length === 0 ||
      activity.kind !== ActivityKind.EVENT ||
      importance.includes(activity.importance),
    (activity: AnyActivity) =>
      selectedSignalTags.size === 0 ||
      activity.kind !== ActivityKind.EVENT ||
      selectedSignalTags.has(activity.signalTag),
    (activity: AnyActivity) =>
      categories.length === 0 ||
      activity.kind !== ActivityKind.EVENT ||
      categories.includes(getEventTheme(activity.eventType)),
    (activity: AnyActivity) =>
      channels.length === 0 || (activity.channel != null && channels.includes(activity.channel))
  ];
};

// Bundle the filter checks into one per-activity predicate. Consumers loop over their own
// containers (grouped buckets, flat lists) and apply this predicate directly.
export const buildFilterPredicate = (
  state: ActivityFeedFilterState
): ((activity: AnyActivity) => boolean) => {
  const checks = buildFilterChecks(state);
  return activity => checks.every(check => check(activity));
};

export type SetFilterState = (
  updater: (prev: ActivityFeedFilterState) => ActivityFeedFilterState
) => void;

// customStartDate / customEndDate are subordinate to date — don't count them as separate filters.
const FILTER_COUNT_EXCLUDE: ReadonlySet<keyof ActivityFeedFilterState> = new Set([
  'customStartDate',
  'customEndDate'
]);

const fieldDiffersFromDefault = (
  key: keyof ActivityFeedFilterState,
  state: ActivityFeedFilterState
): boolean => {
  const val = state[key];
  const def = DEFAULT_FILTER_STATE[key];
  if (Array.isArray(val) && Array.isArray(def)) return arraysDiffer(val, def);
  return val !== def;
};

export const countActiveFilters = (state: ActivityFeedFilterState): number =>
  (Object.keys(state) as (keyof ActivityFeedFilterState)[]).filter(
    key => !FILTER_COUNT_EXCLUDE.has(key) && fieldDiffersFromDefault(key, state)
  ).length;

export const isFiltered = (state: ActivityFeedFilterState): boolean =>
  countActiveFilters(state) > 0;

export const makeArrayToggle =
  <T extends string>(setFilterState: SetFilterState, field: keyof ActivityFeedFilterState) =>
  (val: T): void => {
    setFilterState(prev => {
      const arr = prev[field] as T[];
      return { ...prev, [field]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
    });
  };

export const sortActivities = (activities: AnyActivity[], sortBy: SortBy): AnyActivity[] => {
  const sorted = [...activities];
  if (sortBy === SortBy.MOST_RECENT) {
    sorted.sort((a, b) => eventDateString(b.date).localeCompare(eventDateString(a.date)));
  } else {
    sorted.sort((a, b) => IMPORTANCE_ORDER[a.importance] - IMPORTANCE_ORDER[b.importance]);
  }
  return sorted;
};
